import { Moment } from '@chipinside/frontend'
import { Translate } from '@chipinside/translate'
import { Box, Tooltip } from '@mui/material'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import { isNumber } from 'lodash'
import { Link } from 'react-router-dom'

import { CollarStatus, SanitaryProtocolStatus } from '#/@types/backend'
import NewBadge from '#/components/badges/NewBadge'
import { AvatarBatch } from '#/components/FormatCell/Types/AvatarLink/Avatar/batch'
import { AvatarAnimal } from '#/components/FormatCell/Types/AvatarLink/Avatar/collar'
import { AvatarProtocol } from '#/components/FormatCell/Types/AvatarLink/Avatar/protocol'
import TypeInapt from '#/components/FormatCell/Types/inapt'
import { useFarmPlan, useFarmSlug } from '#/store/ducks/farm'
import { PageId, PagesService } from '#/store/services/Pages/PagesService'
import { usePages } from '#/store/services/Pages/usePages'
import Colors from '#/styles/Colors'
import Fonts from '#/styles/Fonts'
import { getLink, getLinkTooltip } from '#/utils/getLink'
import stringLimit from '#/utils/stringLimit'

const Title = styled('span')<{
  discarded?: boolean
}>(({ discarded }) => ({
  color: discarded ? Colors.animal.discard.color : Colors.primary,
  fontSize: Fonts.fontSize.S,
  fontWeight: 700,
  ...(discarded && { textDecoration: 'line-through' }),
}))

const Subtitle = styled('small')(() => ({
  color: Colors.muted,
  display: 'block',
  fontSize: Fonts.fontSize.SS,
}))

const AvatarWrapper = styled(Box)({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
})

const Container = styled(Stack)({
  alignItems: 'center',
  flexDirection: 'row',
  gap: 16,
})

type Props = {
  new?: boolean
  timestamp?: string
  animal?: Animal
  batch?: Batch
  protocol?: Protocol
  collar?: Collar
  tab?: string
}

function getInfos({
  animal,
  batch,
  collar,
  farmSlug,
  isCowConnect,
  pages,
  protocol,
  tab,
}: Props & { farmSlug: string; isCowConnect?: boolean; pages: PagesService }) {
  if (batch) {
    const linkTo = `farm-batch-${tab ?? 'monitoring'}` as any
    const params = { batch: batch.slug, farm: farmSlug }
    return {
      avatar: <AvatarBatch batch={batch} />,
      collar,
      discarded: undefined,
      linkTo,
      subtitle: batch.type_text,
      title: batch.name,
      to: getLink({ id: linkTo, params }),
    }
  }

  if (protocol) {
    const linkTo = `farm-health-protocol-detail-${tab ?? 'applications'}` as any
    const params = { farm: farmSlug, health_protocol: protocol.protocolId }
    return {
      avatar: <AvatarProtocol protocol={protocol} />,
      collar: undefined,
      discarded: undefined,
      linkTo,
      subtitle: animal?.name,
      title: animal?.earring,
      to: getLink({ id: linkTo, params }),
    }
  }

  const discarded = Boolean(animal?.deleted_at)
  let linkTo: PageId = 'farm-animal-daily-monitoring'

  if (tab) {
    linkTo = `farm-animal-${tab}` as PageId
  }

  if (isCowConnect && !pages.getEnabledById(linkTo)) {
    linkTo = animal?.is_female === false ? 'farm-animal-notes' : 'farm-animal-reproduction'
  }

  const params = { animal: animal?.slug ?? '', farm: farmSlug }

  return {
    avatar: <AvatarAnimal collar={collar} discarded={discarded} />,
    collar,
    discarded,
    linkTo,
    subtitle: animal?.name,
    title: animal?.earring,
    to: getLink({ id: linkTo as PageId, params }),
  }
}

function TooltipContent({
  collar,
  discarded,
  linkTo,
}: {
  linkTo: string | undefined
  collar?: Collar
  discarded?: boolean
}) {
  return (
    <>
      {getLinkTooltip(linkTo)}
      {isNumber(collar?.code) && !discarded && (
        <>
          <br />
          <span>
            {Translate({
              messageKey: ['attached_collar', String(collar.code)],
              separator: ': ',
              translate: [true, false],
            })}
          </span>
        </>
      )}
    </>
  )
}

export default function TypeAvatarLink(props: Props) {
  const { animal, timestamp } = props
  const farmSlug = useFarmSlug()
  const { isCowConnect } = useFarmPlan()
  const pages = usePages()
  const { avatar, collar, discarded, linkTo, subtitle, title, to } = getInfos({
    ...props,
    farmSlug,
    isCowConnect,
    pages,
  })

  if (!title) return <span>-</span>

  return (
    <Stack
      direction="row"
      sx={{ alignItems: 'center', justifyContent: 'space-between', my: 0.5 }}
      spacing={3}
      className="noselect"
    >
      <Tooltip title={<TooltipContent linkTo={linkTo} collar={collar} discarded={discarded} />}>
        <Link to={to as string}>
          <Container>
            <AvatarWrapper>
              {avatar}
              <NewBadge timestamp={props.new ? Moment() : timestamp} />
            </AvatarWrapper>
            <Box>
              <Title discarded={discarded}>{title}</Title>
              <Subtitle>{stringLimit(subtitle, 30)}</Subtitle>
            </Box>
          </Container>
        </Link>
      </Tooltip>
      <TypeInapt flag={animal?.inapt} />
    </Stack>
  )
}

export type Animal = {
  earring: string
  name: string
  slug: string
  deleted_at?: string
  monitored?: boolean
  is_female?: boolean
  inapt?: {
    status: string
    status_text: string
    from: string
    expected_return: string
  }
}

export type Collar = {
  code?: number
  status?: CollarStatus
}

export type Batch = {
  name: string
  slug: string
  type_text?: string
  monitored?: boolean
}

export type Protocol = {
  protocolId: string
  type: 'sanitary-protocol' | 'reproductive' | 'model'
  status?: {
    status: SanitaryProtocolStatus
    status_text: string
  }
}
